import { useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"

import { Check, Code, Copy, FileText, Pencil, Save, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import { useUpdateCellMutation } from "../hooks/use-update-cell-mutation"

type PreviewTextDialogProps = {
  children: React.ReactNode
  text: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  trigger?: React.ReactNode
  filename?: string
  columnId?: string
}

export function PreviewTextDialog({
  children,
  text,
  open,
  onOpenChange,
  trigger,
  filename,
  columnId,
}: PreviewTextDialogProps) {
  const { t } = useTranslation()
  const [viewMode, setViewMode] = useState<"rendered" | "raw">("rendered")
  const [copiedType, setCopiedType] = useState<"raw" | "rendered" | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(text)
  const renderedRef = useRef<HTMLDivElement>(null)
  const updateCellMutation = useUpdateCellMutation()

  const canEdit = !!filename && !!columnId

  function renderContent() {
    if (isEditing) {
      return (
        <Textarea
          className="bg-background min-h-[200px] resize-none font-mono text-sm"
          value={editValue}
          onChange={(event) => setEditValue(event.target.value)}
        />
      )
    }
    if (viewMode === "rendered") {
      return (
        <div ref={renderedRef} className="prose prose-sm dark:prose-invert max-w-none break-words">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
        </div>
      )
    }
    return (
      <pre className="font-mono text-sm leading-relaxed break-words whitespace-pre-wrap">
        {children}
      </pre>
    )
  }

  async function handleCopyRaw() {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedType("raw")
      setTimeout(() => setCopiedType(null), 2000)
    } catch {
      // Clipboard API not available
    }
  }

  async function handleCopyRendered() {
    try {
      const renderedText = renderedRef.current?.innerText ?? text
      await navigator.clipboard.writeText(renderedText)
      setCopiedType("rendered")
      setTimeout(() => setCopiedType(null), 2000)
    } catch {
      // Clipboard API not available
    }
  }

  function handleStartEdit() {
    setEditValue(text)
    setIsEditing(true)
  }

  function handleCancelEdit() {
    setEditValue(text)
    setIsEditing(false)
  }

  function handleSave() {
    if (!filename || !columnId) return

    updateCellMutation.mutate(
      { filename, columnId, value: editValue },
      {
        onSuccess: () => {
          setIsEditing(false)
          onOpenChange?.(false)
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="flex max-h-[80vh] flex-col gap-4 sm:max-w-2xl">
        <DialogHeader className="flex-row items-center justify-between gap-4 space-y-0 pr-8">
          <DialogTitle>{t("pages.ai_parser.preview_text_dialog.title")}</DialogTitle>
          <div className="flex items-center gap-2">
            {!isEditing && (
              <>
                <ToggleGroup
                  size="sm"
                  type="single"
                  value={viewMode}
                  onValueChange={(value) => value && setViewMode(value as "rendered" | "raw")}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <ToggleGroupItem aria-label="Rendered view" value="rendered">
                        <FileText className="size-4" />
                      </ToggleGroupItem>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t("pages.ai_parser.preview_text_dialog.rendered") || "Rendered"}
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <ToggleGroupItem aria-label="Raw view" value="raw">
                        <Code className="size-4" />
                      </ToggleGroupItem>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t("pages.ai_parser.preview_text_dialog.raw") || "Raw"}
                    </TooltipContent>
                  </Tooltip>
                </ToggleGroup>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button className="gap-2" size="sm" variant="outline">
                      {copiedType ? (
                        <>
                          <Check className="text-success size-4" />
                          {t("pages.ai_parser.preview_text_dialog.copied") || "Copied"}
                        </>
                      ) : (
                        <>
                          <Copy className="size-4" />
                          {t("pages.ai_parser.preview_text_dialog.copy") || "Copy"}
                        </>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      className={cn(copiedType === "raw" && "text-success")}
                      onClick={handleCopyRaw}
                    >
                      <Code className="mr-2 size-4" />
                      {t("pages.ai_parser.preview_text_dialog.copy_raw") || "Copy raw"}
                      {copiedType === "raw" && <Check className="ml-auto size-4" />}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className={cn(copiedType === "rendered" && "text-success")}
                      onClick={handleCopyRendered}
                    >
                      <FileText className="mr-2 size-4" />
                      {t("pages.ai_parser.preview_text_dialog.copy_rendered") || "Copy rendered"}
                      {copiedType === "rendered" && <Check className="ml-auto size-4" />}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                {canEdit && (
                  <Button className="gap-2" size="sm" variant="outline" onClick={handleStartEdit}>
                    <Pencil className="size-4" />
                    {t("pages.ai_parser.preview_text_dialog.edit") || "Edit"}
                  </Button>
                )}
              </>
            )}
            {isEditing && (
              <>
                <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                  <X className="size-4" />
                  {t("pages.ai_parser.preview_text_dialog.cancel") || "Cancel"}
                </Button>
                <Button disabled={updateCellMutation.isPending} size="sm" onClick={handleSave}>
                  {updateCellMutation.isPending ? (
                    <Spinner className="size-4" />
                  ) : (
                    <Save className="size-4" />
                  )}
                  {t("pages.ai_parser.preview_text_dialog.save") || "Save"}
                </Button>
              </>
            )}
          </div>
        </DialogHeader>
        <div className="bg-muted/50 flex-1 overflow-y-auto rounded-md p-4">{renderContent()}</div>
      </DialogContent>
    </Dialog>
  )
}
